import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Plane, Users, ChevronRight } from "lucide-react";
import { DashboardLayout } from "@/presentation/layouts/DashboardLayout";
import { useAuth } from "@/application/hooks/useAuth";
import { useTrips } from "@/application/hooks/useTrips";
import { TripCard } from "@/presentation/components/TripCard";
import { EmptyState } from "@/presentation/components/EmptyState";
import { ErrorState } from "@/presentation/components/ErrorState";
import { FeatureHighlights } from "@/presentation/components/FeatureHighlights";
import { SkeletonList } from "@/presentation/components/LoadingState";
import { CreateTripDialog } from "@/presentation/components/CreateTripDialog";
import { OnboardingModal } from "@/presentation/components/onboarding";
import { BalanceSummaryCard } from "@/presentation/components/BalanceSummaryCard";
import { TodaySchedulePanel } from "@/presentation/components/TodaySchedulePanel";
import { NextStepsPanel } from "@/presentation/components/NextStepsPanel";
import { useState, useMemo } from "react";
import { computeTripStatus } from "@tabipla/shared";

// Helper to get random item from array
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: trips, isLoading, isError, error, refetch } = useTrips();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Filter upcoming trips (computed status: planning or confirmed)
  const upcomingTrips = useMemo(() => {
    return (
      trips?.filter((trip) => {
        const status = computeTripStatus(trip.startDate, trip.endDate);
        return status === "planning" || status === "confirmed";
      }) ?? []
    );
  }, [trips]);

  // Filter recent trips (last 5 completed trips)
  const recentTrips = useMemo(() => {
    return (
      trips
        ?.filter((trip) => {
          const status = computeTripStatus(trip.startDate, trip.endDate);
          return status === "completed";
        })
        .slice(0, 5) ?? []
    );
  }, [trips]);

  // Memoize random greeting to keep it stable during the session
  const { greeting, welcomeMessage } = useMemo(() => {
    const hour = new Date().getHours();

    // Get time-based greeting variations
    let greetingVariations: string[];
    if (hour < 12) {
      greetingVariations = t("home.greetings.morningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.morning")],
      }) as string[];
    } else if (hour < 18) {
      greetingVariations = t("home.greetings.afternoonVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.afternoon")],
      }) as string[];
    } else {
      greetingVariations = t("home.greetings.eveningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.evening")],
      }) as string[];
    }

    // Get welcome message variations
    const welcomeVariations = t("home.welcomeVariations", {
      returnObjects: true,
      defaultValue: [t("home.welcomeBack", { name: "{{name}}" })],
    }) as string[];

    return {
      greeting: getRandomItem(greetingVariations),
      welcomeMessage: getRandomItem(welcomeVariations),
    };
  }, [t]);

  // Show error state (after all hooks)
  if (isError && error) {
    return (
      <DashboardLayout>
        <ErrorState error={error} onRetry={() => refetch()} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      fabAction={{
        icon: Plus,
        label: t("trips.createNew"),
        onClick: () => setShowCreateDialog(true),
      }}
    >
      <div className="animate-fade-in space-y-8">
        {/* Greeting Section */}
        <section className="space-y-1">
          <p className="text-muted-foreground">{greeting}</p>
          <h1 className="text-2xl font-bold tracking-tight">
            {user?.name ? welcomeMessage.replace("{{name}}", user.name) : t("home.welcome")}
          </h1>
        </section>

        {/* Today's Schedule Panel - Shows when traveling */}
        {trips && trips.length > 0 && <TodaySchedulePanel trips={trips} />}

        {/* Feature Highlights for New Users */}
        {!isLoading && (!trips || trips.length === 0) && (
          <FeatureHighlights onCreateTrip={() => setShowCreateDialog(true)} />
        )}

        {/* Upcoming Trips Carousel */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t("home.upcomingTrips")}</h2>
            {upcomingTrips.length > 0 && (
              <button
                onClick={() => navigate("/trips")}
                className="flex items-center text-sm text-primary hover:underline"
              >
                {t("common.viewAll")}
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {isLoading ? (
            <SkeletonList count={2} />
          ) : upcomingTrips.length > 0 ? (
            <div className="space-y-3">
              {upcomingTrips.slice(0, 3).map((trip) => (
                <TripCard key={trip.id} trip={trip} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Plane}
              title={t("onboarding.emptyHome.title")}
              description={t("onboarding.emptyHome.description")}
              action={{
                label: t("onboarding.emptyHome.cta"),
                onClick: () => setShowCreateDialog(true),
                icon: Plane,
              }}
              variant="dashed"
            />
          )}
        </section>

        {/* Next Steps Panel - Shows actionable tasks */}
        {trips && trips.length > 0 && <NextStepsPanel trips={trips} />}

        {/* Quick Actions */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t("home.quickActions.title")}</h2>
          <div className="space-y-2">
            {/* Create Trip Action */}
            <button
              onClick={() => setShowCreateDialog(true)}
              className="flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left transition-all hover:bg-accent hover:shadow-sm active:scale-[0.99]"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                <Plane className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{t("home.quickActions.createTrip")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("home.quickActions.createTripDesc")}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </button>

            {/* Create Group Action */}
            <button
              onClick={() => navigate("/groups")}
              className="flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left transition-all hover:bg-accent hover:shadow-sm active:scale-[0.99]"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300">
                <Users className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{t("home.quickActions.createGroup")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("home.quickActions.createGroupDesc")}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </button>
          </div>
        </section>

        {/* Recent Trips */}
        {(isLoading || recentTrips.length > 0) && (
          <section className="space-y-4">
            <h2 className="text-lg font-semibold">{t("home.recentTrips")}</h2>
            {isLoading ? (
              <SkeletonList count={2} />
            ) : (
              <div className="space-y-3">
                {recentTrips.map((trip) => (
                  <TripCard key={trip.id} trip={trip} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Balance Summary - Secondary feature at bottom */}
        <BalanceSummaryCard />
      </div>

      <OnboardingModal />
      <CreateTripDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </DashboardLayout>
  );
}
